/**
 * Utilitaires de validation email avancée pour TeamManagement
 * Corrige le feedback Viguen sur la validation insuffisante
 */

// Regex stricte pour validation email (RFC 5322 compliant)
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// Domaines personnels à rejeter (non professionnels)
const PERSONAL_DOMAINS = [
  // Grands fournisseurs internationaux
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'live.com', 'icloud.com', 'aol.com', 'protonmail.com',
  'mail.com', 'gmx.com', 'zoho.com', 'yandex.com',
  
  // Français
  'free.fr', 'orange.fr', 'sfr.fr', 'wanadoo.fr', 'laposte.net',
  'bbox.fr', 'numericable.fr', 'club-internet.fr', 'cegetel.net',
  
  // Autres pays européens
  'web.de', 'gmx.de', 't-online.de', 'freenet.de',
  'libero.it', 'virgilio.it', 'tiscali.it',
  'mail.ru', 'yandex.ru', 'rambler.ru',
  
  // Asie
  'qq.com', '163.com', '126.com', 'sina.com',
  'naver.com', 'daum.net', 'hanmail.net'
];

// Domaines temporaires/jetables à rejeter
const DISPOSABLE_DOMAINS = [
  'mailinator.com', 'guerrillamail.com', 'temp-mail.org',
  'yopmail.com', '10minutemail.com', 'throwaway.email',
  'maildrop.cc', 'sharklasers.com', 'trashmail.com',
  'tempmail.net', 'getnada.com', 'mohmal.com',
  'mailcatch.com', 'mailnesia.com', 'spamgourmet.com'
];

export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

/**
 * Valide le format syntaxique d'un email
 */
export function validateEmailFormat(email: string): EmailValidationResult {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Adresse email requise' };
  }

  const cleanEmail = email.toLowerCase().trim();
  
  // Vérifications spécifiques AVANT la regex générale
  if (cleanEmail.includes(' ')) {
    return { isValid: false, error: 'L\'email ne peut pas contenir d\'espaces' };
  }

  if (cleanEmail.includes('..')) {
    return { isValid: false, error: 'L\'email ne peut pas contenir de points consécutifs' };
  }

  if (cleanEmail.startsWith('.') || cleanEmail.endsWith('.')) {
    return { isValid: false, error: 'L\'email ne peut pas commencer ou finir par un point' };
  }

  // Vérification regex générale
  if (!EMAIL_REGEX.test(cleanEmail)) {
    return { isValid: false, error: 'Format d\'email invalide' };
  }

  // Vérifier qu'il y a bien une extension de domaine
  const parts = cleanEmail.split('@');
  if (parts.length !== 2) {
    return { isValid: false, error: 'Format d\'email invalide' };
  }
  
  const domain = parts[1];
  if (!domain.includes('.')) {
    return { isValid: false, error: 'Format d\'email invalide' };
  }

  return { isValid: true };
}

/**
 * Valide le domaine de l'email (professionnel vs personnel)
 */
export function validateEmailDomain(email: string): EmailValidationResult {
  const domain = email.toLowerCase().split('@')[1];
  
  if (!domain) {
    return { isValid: false, error: 'Domaine email manquant' };
  }

  if (DISPOSABLE_DOMAINS.includes(domain)) {
    return {
      isValid: false,
      error: 'Les adresses email temporaires ne sont pas autorisées'
    };
  }

  if (PERSONAL_DOMAINS.includes(domain)) {
    return {
      isValid: false,
      error: 'Veuillez utiliser une adresse email professionnelle (pas Gmail, Yahoo, etc.)'
    };
  }

  // Vérification domaine trop court (probable erreur)
  if (domain.length < 4) {
    return {
      isValid: false,
      error: 'Le domaine email semble invalide'
    };
  }

  return { isValid: true };
}

/**
 * Validation complète d'un email (format + domaine)
 */
export function validateEmail(email: string): EmailValidationResult {
  // 1. Validation format
  const formatResult = validateEmailFormat(email);
  if (!formatResult.isValid) {
    return formatResult;
  }

  // 2. Validation domaine
  const domainResult = validateEmailDomain(email);
  if (!domainResult.isValid) {
    return domainResult;
  }

  return { isValid: true };
}

/**
 * Normalise un email (lowercase, trim)
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Extrait le domaine d'un email
 */
export function extractDomain(email: string): string | null {
  const parts = email.split('@');
  return parts.length === 2 ? parts[1].toLowerCase() : null;
}

/**
 * Vérifie si un domaine est professionnel
 */
export function isProfessionalDomain(domain: string): boolean {
  const lowerDomain = domain.toLowerCase();
  return !PERSONAL_DOMAINS.includes(lowerDomain) && 
         !DISPOSABLE_DOMAINS.includes(lowerDomain);
}

/**
 * Validation rapide pour email professionnel (pour Zod schemas)
 * Version simplifiée sans vérification de format (Zod s'en charge)
 */
export function quickValidateProfessionalEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const domain = email.toLowerCase().split('@')[1];
  if (!domain) {
    return false;
  }

  // Vérification rapide des domaines interdits
  return !PERSONAL_DOMAINS.includes(domain) && !DISPOSABLE_DOMAINS.includes(domain);
}

/**
 * Validation professionnelle compatible avec l'ancien système
 * DÉPRÉCIÉ : Utiliser checkEmailProfessional() de emailCompanyCoherence.ts
 * Maintenu temporairement pour compatibilité
 */
export function validateProfessionalEmail(email: string): boolean {
  console.warn('validateProfessionalEmail() est déprécié. Utiliser checkEmailProfessional() de emailCompanyCoherence.ts');
  const result = validateEmail(email);
  return result.isValid;
}

/**
 * Valide qu'un référent ne s'auto-référence pas
 * Vérifie que l'email du référent est différent de celui du candidat
 */
export function validateNotSelfReferencing(referentEmail: string, candidateEmail: string): boolean {
  if (!referentEmail || !candidateEmail) {
    return true; // Si l'un des emails est vide, pas d'auto-référencement
  }
  
  return normalizeEmail(referentEmail) !== normalizeEmail(candidateEmail);
}

/**
 * Valide tous les emails de référents en lot
 * Utilisé pour la validation optimisée des formulaires
 */
export function validateAllReferentEmails(referents: Array<{ email: string }>): {
  isValid: boolean;
  errors: Array<{ index: number; error: string }>;
} {
  const errors: Array<{ index: number; error: string }> = [];
  
  referents.forEach((referent, index) => {
    const validation = validateEmail(referent.email);
    if (!validation.isValid && validation.error) {
      errors.push({ index, error: validation.error });
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validation basique du format email pour les candidats
 * Moins stricte que la validation professionnelle (accepte Gmail, etc.)
 * Utilisé pour les candidats qui peuvent avoir des emails personnels
 */
export const validateCandidateEmailFormat = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  const cleanEmail = email.toLowerCase().trim();
  
  // Vérifications basiques
  if (cleanEmail.includes(' ') || cleanEmail.includes('..')) {
    return false;
  }
  
  if (cleanEmail.startsWith('.') || cleanEmail.endsWith('.')) {
    return false;
  }
  
  // Vérification regex
  if (!EMAIL_REGEX.test(cleanEmail)) {
    return false;
  }
  
  // Vérifier qu'il y a bien un @ et un domaine avec extension
  const parts = cleanEmail.split('@');
  if (parts.length !== 2) {
    return false;
  }
  
  const domain = parts[1];
  if (!domain.includes('.') || domain.length < 4) {
    return false;
  }
  
  return true;
};

/**
 * Validation avec message d'erreur pour les candidats
 * Retourne un objet avec isValid et message optionnel
 */
export const validateCandidateEmailWithMessage = (email: string): { 
  isValid: boolean; 
  message?: string 
} => {
  if (!email || !email.trim()) {
    return { isValid: false, message: 'L\'email est requis' };
  }
  
  if (!validateCandidateEmailFormat(email)) {
    return { isValid: false, message: 'Format d\'email invalide' };
  }
  
  return { isValid: true };
};